type OrderedUuidTuple = Readonly<[string, string, string, string, string]>;

interface OrderedUuidReader {
  (input: string): OrderedUuidTuple;
}

interface OrderedUuidWriter {
  (input: OrderedUuidTuple): string;
}

export class InvalidOrderedUuidError extends Error {}

// eslint-disable-next-line @typescript-eslint/no-magic-numbers --- These are predefined numbers in the UUID specification
const UuidSegmentLength = [8, 4, 4, 4, 12];
const V3UuidChars = 32;
const V2UuidChars = 36;
const V1UuidChars = 36;

const isValidUuidTuple = (input: string[] | Readonly<string[]>): input is OrderedUuidTuple => {
  // Same number of pieces
  if (input.length !== UuidSegmentLength.length) {
    return false;
  }

  // Every piece has the matching format length
  return input.every((s, i) => typeof s === "string" && s.length === UuidSegmentLength[i]);
};

export const fromV3: OrderedUuidReader = (input) => {
  if (input.length > V3UuidChars) {
    throw new InvalidOrderedUuidError(`fromV3("${input}") was not a valid UUID input`);
  }
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers --- These are predifined positional numbers
  return [input.slice(8, 16), input.slice(4, 8), input.slice(0, 4), input.slice(16, 20), input.slice(20, 32)];
};

export const fromV2: OrderedUuidReader = (input) => {
  if (input.length > V2UuidChars) {
    throw new InvalidOrderedUuidError(`fromV2("${input}") was not a valid UUID input`);
  }
  const pieces = input.split("-");

  return [pieces[2] ?? "", pieces[1] ?? "", pieces[0] ?? "", pieces[3] ?? "", pieces[4] ?? ""];
};

export const fromV1: OrderedUuidReader = (input) => {
  if (input.length > V1UuidChars) {
    throw new InvalidOrderedUuidError(`fromV1("${input}") was not a valid UUID input`);
  }
  const pieces = input.split("-");

  return [pieces[0] ?? "", pieces[1] ?? "", pieces[2] ?? "", pieces[3] ?? "", pieces[4] ?? ""];
};

export const toV3: OrderedUuidWriter = (input) => `${input[2]}${input[1]}${input[0]}${input[3]}${input[4]}`;
export const toV2: OrderedUuidWriter = (input) => `${input[2]}-${input[1]}-${input[0]}-${input[3]}-${input[4]}`;
export const toV1: OrderedUuidWriter = (input) => `${input[0]}-${input[1]}-${input[2]}-${input[3]}-${input[4]}`;

export const convert = (input: string, from: OrderedUuidReader, to: OrderedUuidWriter) => {
  const pieces = from(input);

  if (!isValidUuidTuple(pieces)) {
    throw new InvalidOrderedUuidError(`${from.name}("${input}") was not a valid UUID input`);
  }

  return to(pieces);
};

export const safeConvert = (input: string, from: OrderedUuidReader, to: OrderedUuidWriter) => {
  try {
    return convert(input, from, to);
  } catch {
    return input;
  }
};
